import {
  useGetAllDepartmentsQuery,
  useGetAllDesignationsQuery,
  useGetAllEmployeesQuery,
} from "@/app/features/employee/emp-dashboard/emp-dashboard.api";
// import { useGetSalaryGradeQuery } from '@/app/features/employee/emp-payroll/emp-payroll.api';
import {
  useCreateJobPostMutation,
  useGetSingleJobPostQuery,
  useUpdateJobPostMutation,
} from "@/app/features/recruitment/job-applicants/job-applicants.api";
import CustomDrawer from "@/components/CustomDrawer";
import type { CustomModalProps } from "@/components/CustomModal";
import WithLoader from "@/components/WithLoader";
import { COUNTRY_OPTIONS } from "@/constants/countries";
import useSelectionOptions from "@/hooks/useSelectionOptions";
import useDiscloser from "@/hooks/useDiscloser";
import { Calendar03Icon, ChevronDownIcon, PencilEdit02Icon } from "@/icons";
import { getErrorMessage } from "@/lib/error";
import { toast } from "@/lib/toast";
import ManageDepartment from "@/pages/Admin/Employee/Dashboard/_components/CreateEmployee/ManageDepartment";
import ManageDesignation from "@/pages/Admin/Employee/Dashboard/_components/CreateEmployee/ManageDesignation";
import {
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { NODE_ENV } from "@/config/global.config";
import { cn } from "@/lib/cn";

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link"],
    ["clean"],
  ],
};

const QUILL_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "align",
  "link",
];

const initialValues = {
  // Basic Info
  job_title: "Lead Data Scientist",
  employee_type: "full-time",
  location: "Bangladesh",
  publish_date: dayjs().add(2, "day"),
  job_application_deadline: dayjs().add(18, "day"),
  hiring_date: dayjs().add(40, "day"),

  // Role
  department_id: "cbc4ef7b-68f4-4224-b5f9-876e86633a36",
  position_id: "bf77e9af-ffff-4515-be65-6b60f7391127",
  vacancy_count: 1,
  evaluator_user_id: "85abf10c-1f9d-4993-a97c-cac962e1dde4",
  job_experience: "5-8 years",

  // Salary
  salary_min_bdt: 120000,
  salary_max_bdt: 180000,
  salary_type: "MONTHLY",
  is_show_salary_public: false,

  // CV Scoring
  interview_passing_rate_percentage: 80,
  cv_accepted_score_percentage: 72,

  // Functional Keywords
  functional_screening_keywords: [
    "Machine Learning",
    "Statistical Modeling",
    "Data Pipeline Design",
    "A/B Testing & Experimentation",
  ],
  functional_keyword_weightage: 45,

  // Technical Keywords
  tactical_screening_keywords: [
    "Python (Pandas, NumPy, Scikit-learn)",
    "TensorFlow / PyTorch",
    "SQL & NoSQL Databases",
    "Apache Spark",
  ],
  tactical_keyword_weightage: 35,

  // Prerequisite Keywords
  prerequisite_screening_keywords: [
    "Linear Algebra & Calculus",
    "Probability & Statistics",
    "Git & Version Control",
    "Data Cleaning & EDA",
  ],
  prerequisite_keyword_weightage: 20,

  // Skills
  skills: [
    "Python",
    "R",
    "TensorFlow",
    "Scikit-learn",
    "Apache Spark",
    "Tableau",
    "dbt",
    "BigQuery",
  ],

  // Rich-text Content
  job_context:
    "<p>We are looking for a <strong>Lead Data Scientist</strong> to join our Analytics & AI division. You will spearhead the development of predictive models, workforce intelligence features, and data-driven insights that power the HRX platform for enterprise clients across South Asia.</p>",
  job_description:
    "<ul><li>Design, develop, and deploy machine learning models for HR analytics (attrition, performance prediction, talent matching)</li><li>Own the end-to-end ML lifecycle from data sourcing to production monitoring</li><li>Mentor junior data scientists and establish best practices for model development</li><li>Collaborate with product and engineering to embed AI features into the platform</li><li>Present model findings and business impact to C-level stakeholders</li></ul>",
  educational_experience:
    "<p>Master's or Ph.D. in <strong>Computer Science, Statistics, Mathematics, or a related quantitative field</strong>. Equivalent industry experience with a strong publication or project track record is also considered.</p>",
  experience_requirement:
    "<p>At least <strong>5 years</strong> of hands-on data science experience, including a minimum of <strong>2 years</strong> leading data science initiatives or teams in a production environment.</p>",
  additional_requirement:
    "<ul><li>Proven experience deploying ML models to production (REST APIs, batch pipelines)</li><li>Familiarity with MLOps practices and tools (MLflow, Kubeflow, or similar)</li><li>Experience working with large-scale distributed datasets</li><li>Strong business acumen and ability to translate data insights into actionable decisions</li></ul>",
  compensation_and_benefit:
    "<ul><li>Gross monthly salary BDT 1,20,000 – 1,80,000 (based on experience)</li><li>Annual performance-linked bonus up to 20% of CTC</li><li>Medical insurance for employee and immediate family</li><li>Research & conference travel allowance</li><li>Flexible hybrid work arrangement</li><li>Festival bonuses and 21 days earned leave</li></ul>",
};

type Props = CustomModalProps & { id?: string };

export default function ManageJobPost({ open, handleClose, id }: Props) {
  const [form] = Form.useForm();
  const status = Form.useWatch("status", form);
  const publishDate = Form.useWatch("publish_date", form);
  const deadlineDate = Form.useWatch("job_application_deadline", form);
  const departmentId = Form.useWatch("department_id", form);
  const positionId = Form.useWatch("position_id", form);

  const [subConfig, handleSubOpen, handleSubClose] = useDiscloser<
    "MANAGE_DEPARTMENT" | "MANAGE_DESIGNATION"
  >();

  const { data: departmentsData, isLoading: isDepartmentQueryLoading } =
    useGetAllDepartmentsQuery(
      { params: { status: "active" } },
      { skip: !open },
    );

  const { data: designationsData, isLoading: isDesignationQueryLoading } =
    useGetAllDesignationsQuery(
      { params: { status: "active" } },
      { skip: !open },
    );

  // ------ API Queries ------
  const { data: jobPostData, isLoading: isFetchingJobPost } =
    useGetSingleJobPostQuery(id!, {
      skip: !id || !open,
    });
  // const { data: gradesData } = useGetSalaryGradeQuery(null, { skip: !open });
  // const selectedGradeId = Form.useWatch('grade_id', form);

  // ------ API Mutations ------
  const [createJobPost, { isLoading: isCreating }] = useCreateJobPostMutation();
  const [updateJobPost, { isLoading: isUpdating }] = useUpdateJobPostMutation();

  const isSubmitting = isCreating || isUpdating;

  // ------ Select Options Hooks ------
  // const [departments, isDeptLoading] = useSelectionOptions(
  //   useGetAllDepartmentsQuery,
  //   {
  //     labelKey: "name",
  //     valueKey: "id",
  //     params: { params: { status: "active" } },
  //     skip: !open,
  //   },
  // );

  // const [designations, isDesignationLoading] = useSelectionOptions(
  //   useGetAllDesignationsQuery,
  //   {
  //     labelKey: "name",
  //     valueKey: "id",
  //     params: { params: { status: "active" } },
  //     skip: !open,
  //   },
  // );

  const [evaluatorOptions, isEmployeesLoading] = useSelectionOptions(
    useGetAllEmployeesQuery,
    {
      labelKey: "name",
      valueKey: "user_id",
      skip: !open,
    },
  );

  // const [gradeOptions, isGradeLoading] = useSelectionOptions(
  //   useGetSalaryGradeQuery,
  //   {
  //     labelKey: 'grade_name',
  //     valueKey: 'id',
  //     dataKey: 'grades',
  //     skip: !open,
  //     transformLabel: (item: any) => snakeToCamel(item.grade_name || ''),
  //   },
  // );

  // Set form values when editing
  useEffect(() => {
    if (id && jobPostData?.data) {
      const data = jobPostData.data;
      form.setFieldsValue({
        ...data,
        job_application_deadline: data.job_application_deadline
          ? dayjs(data.job_application_deadline)
          : undefined,
        publish_date: data.publish_date ? dayjs(data.publish_date) : undefined,
        hiring_date: data.hiring_date ? dayjs(data.hiring_date) : undefined,
        salary_min_bdt:
          data.salary_min_bdt !== undefined && data.salary_min_bdt !== null
            ? Number(data.salary_min_bdt)
            : undefined,
        salary_max_bdt:
          data.salary_max_bdt !== undefined && data.salary_max_bdt !== null
            ? Number(data.salary_max_bdt)
            : undefined,
      });
    } else if (!id) {
      form.resetFields();
    }
  }, [id, jobPostData, form]);

  const handleSubmit = async (values: any) => {
    const { hiring_date, publish_date, job_application_deadline, ...rest } =
      values || {};
    const status = !publish_date
      ? "DRAFT"
      : dayjs(publish_date).isAfter(dayjs(), "day")
        ? "DRAFT"
        : "PUBLISHED";

    const minSalary =
      rest.salary_min_bdt !== undefined && rest.salary_min_bdt !== null
        ? Number(rest.salary_min_bdt)
        : undefined;
    const maxSalary =
      rest.salary_max_bdt !== undefined && rest.salary_max_bdt !== null
        ? Number(rest.salary_max_bdt)
        : undefined;

    const formattedValues = {
      ...rest,
      status,
      salary_min_bdt: minSalary,
      salary_max_bdt: maxSalary,
      hiring_date: hiring_date?.format("YYYY-MM-DD"),
      publish_date: publish_date?.format("YYYY-MM-DD"),
      job_application_deadline: job_application_deadline?.format("YYYY-MM-DD"),
    };

    try {
      if (id) {
        // Update existing job post
        await updateJobPost({
          id,
          body: formattedValues,
        }).unwrap();
        toast("success", "Job post updated successfully");
      } else {
        // Create new job post
        await createJobPost({ body: formattedValues }).unwrap();
        toast("success", "Job post created successfully");
      }
      // Show success message (you can use antd message or notification)
      form.resetFields();
      handleClose();
    } catch (error) {
      const message = getErrorMessage(error, "Failed to create job post");
      toast("error", message);
      // Show error message (you can use antd message or notification)
    }
  };

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={() => {
        handleClose();
        form.resetFields();
      }}
      title={"Create New Job Post"}
    >
      <WithLoader
        isLoading={isFetchingJobPost}
        className={cn(
          "rounded-lg",
          isFetchingJobPost && "h-[calc(100vh_-_105px)]",
        )}
      >
        <Form
          form={form}
          size="large"
          layout="vertical"
          initialValues={NODE_ENV === "development" ? initialValues : {}}
          className="custom-form space-y-6"
          onFinish={handleSubmit}
        >
          <div className="grid grid-cols-2 gap-6">
            <div className="col-span-2">
              <div className="justify-start text-white text-xl font-bold">
                Basic Info :
              </div>
              <Divider className="border-slate-600 mt-3 mb-0" />
            </div>
            <Form.Item
              label="Job Title"
              name="job_title"
              rules={[
                {
                  required: true,
                  message: "Please enter job title",
                },
              ]}
            >
              <Input
                className="transparent-input"
                placeholder="e.g. Junior Software Engineer"
              />
            </Form.Item>

            <Form.Item
              label="Employee Type"
              name="employee_type"
              rules={[
                {
                  required: true,
                  message: "Please select employee type",
                },
              ]}
            >
              <Select
                placeholder="Select"
                className="transparent-select"
                suffixIcon={
                  <ChevronDownIcon size={24} className="text-white" />
                }
                options={[
                  { value: "full-time", label: "Full-time" },
                  { value: "part-time", label: "Part-time" },
                  { value: "contract", label: "Contract" },
                ]}
              />
            </Form.Item>

            <Form.Item label="Publish Date" name="publish_date">
              <DatePicker
                autoFocus
                picker="date"
                format="DD MMM, YYYY"
                placeholder="Select date"
                style={{ width: "100%" }}
                className="transparent-picker"
                suffixIcon={<Calendar03Icon size={20} className="text-white" />}
                onChange={() => {
                  form.setFieldsValue({
                    job_application_deadline: undefined,
                    hiring_date: undefined,
                  });
                }}
              />
            </Form.Item>

            <Form.Item
              label="Job Application Deadline"
              name="job_application_deadline"
              rules={[
                {
                  required: true,
                  message: "Please select application deadline",
                },
              ]}
            >
              <DatePicker
                picker="date"
                format="DD MMM, YYYY"
                placeholder="Select date"
                style={{ width: "100%" }}
                className="transparent-picker"
                disabled={!publishDate}
                disabledDate={(current) => {
                  const today = dayjs().startOf("day");
                  if (publishDate) {
                    const pub = dayjs(publishDate).startOf("day");
                    return (
                      current &&
                      (current.isBefore(pub) || current.isBefore(today))
                    );
                  }
                  return current && current < today;
                }}
                suffixIcon={<Calendar03Icon size={20} className="text-white" />}
                onChange={() => {
                  form.setFieldsValue({
                    hiring_date: undefined,
                  });
                }}
              />
            </Form.Item>

            <Form.Item
              label="Hiring Date"
              name="hiring_date"
              rules={[
                {
                  required: true,
                  message: "Please select hiring date",
                },
              ]}
            >
              <DatePicker
                picker="date"
                format="DD MMM, YYYY"
                placeholder="Select date"
                style={{ width: "100%" }}
                className="transparent-picker"
                disabled={!deadlineDate}
                disabledDate={(current) => {
                  const today = dayjs().startOf("day");
                  if (deadlineDate) {
                    const dd = dayjs(deadlineDate).startOf("day");
                    return (
                      current &&
                      (current.isBefore(dd) || current.isBefore(today))
                    );
                  }
                  return current && current < today;
                }}
                suffixIcon={<Calendar03Icon size={20} className="text-white" />}
              />
            </Form.Item>

            <Form.Item
              label="Country"
              name="location"
              rules={[
                {
                  required: true,
                  message: "Please select location",
                },
              ]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Select"
                className="transparent-select"
                suffixIcon={
                  <ChevronDownIcon size={24} className="text-white" />
                }
                options={COUNTRY_OPTIONS}
              />
            </Form.Item>

            <div>
              <div className="flex justify-between items-center mt-2 mb-3">
                <span className="text-sm text-white">Department *</span>
                <button
                  type="button"
                  onClick={() => handleSubOpen({ type: "MANAGE_DEPARTMENT" })}
                >
                  <PencilEdit02Icon
                    size={20}
                    className="text-gray-300 hover:text-white cursor-pointer"
                  />
                </button>
              </div>
              <Form.Item
                name="department_id"
                rules={[
                  { required: true, message: "Please select department" },
                ]}
              >
                <WithLoader isLoading={isDepartmentQueryLoading}>
                  <CustomCheckbox
                    className="custom-check"
                    value={departmentId}
                    onChange={(val: any) => {
                      form.setFieldValue("department_id", val);
                    }}
                    options={departmentsData?.data?.data}
                  />
                </WithLoader>
              </Form.Item>
            </div>

            <div>
              <div className="flex justify-between items-center mt-2 mb-3">
                <span className="text-sm text-white">Designation *</span>
                <button
                  type="button"
                  onClick={() => handleSubOpen({ type: "MANAGE_DESIGNATION" })}
                >
                  <PencilEdit02Icon
                    size={20}
                    className="text-gray-300 hover:text-white cursor-pointer"
                  />
                </button>
              </div>
              <Form.Item
                name="position_id"
                rules={[
                  { required: true, message: "Please select designation" },
                ]}
              >
                <WithLoader isLoading={isDesignationQueryLoading}>
                  <CustomCheckbox
                    className="custom-check"
                    value={positionId}
                    onChange={(val: any) => {
                      form.setFieldValue("position_id", val);
                    }}
                    options={designationsData?.data?.data}
                  />
                </WithLoader>
              </Form.Item>
            </div>

            <Form.Item
              label="Number Of Vacancies"
              name="vacancy_count"
              rules={[
                {
                  required: true,
                  message: "Please enter number of vacancies",
                },
                {
                  type: "number",
                  min: 1,
                  message: "Must be at least 1",
                },
              ]}
            >
              <InputNumber
                className="transparent-input w-full"
                controls={false}
                placeholder="e.g. 3"
              />
            </Form.Item>

            <Form.Item
              label="Assign Evaluator"
              name="evaluator_user_id"
              rules={[
                {
                  required: true,
                  message: "Please select evaluator",
                },
              ]}
            >
              <Select
                placeholder="Select"
                className="transparent-select"
                suffixIcon={
                  <ChevronDownIcon size={24} className="text-white" />
                }
                showSearch
                optionFilterProp="label"
                loading={isEmployeesLoading}
                options={evaluatorOptions}
              />
            </Form.Item>

            <Form.Item
              label="Job Experience"
              name="job_experience"
              rules={[
                {
                  required: true,
                  message: "Please enter job experience",
                },
              ]}
            >
              <Input
                className="transparent-input"
                placeholder="e.g. 5-7 years"
              />
            </Form.Item>

            {/* <Form.Item
              label="Assign Grade"
              name="grade_id"
              rules={[
                {
                  required: true,
                  message: 'Please select grade',
                },
              ]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Select"
                className="transparent-select"
                suffixIcon={
                  <ChevronDownIcon size={24} className="text-white" />
                }
                loading={isGradeLoading}
                options={gradeOptions}
              />
            </Form.Item>

            <Form.Item
              label="Salary Range (Internal Reference)"
              name="salary_range"
            >
              <Input
                disabled
                className="transparent-input"
                placeholder="e.g. 50,000 - 80,000"
              />
            </Form.Item> */}

            <Form.Item
              label="Minimum Salary (BDT)"
              name="salary_min_bdt"
              rules={[
                {
                  required: true,
                  message: "Please enter minimum salary",
                },
                {
                  type: "number",
                  min: 0,
                  message: "Minimum salary must be at least 0",
                },
              ]}
            >
              <InputNumber
                className="transparent-input w-full"
                controls={false}
                placeholder="e.g. 50000"
              />
            </Form.Item>

            <Form.Item
              label="Maximum Salary (BDT)"
              name="salary_max_bdt"
              dependencies={["salary_min_bdt"]}
              rules={[
                {
                  required: true,
                  message: "Please enter maximum salary",
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const min = getFieldValue("salary_min_bdt");
                    if (value && min && Number(value) < Number(min)) {
                      return Promise.reject(
                        new Error(
                          "Maximum salary must be greater than or equal to minimum salary",
                        ),
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputNumber
                className="transparent-input w-full"
                controls={false}
                placeholder="e.g. 80000"
              />
            </Form.Item>

            <Form.Item
              name="salary_type"
              hidden
              initialValue="MONTHLY"
              noStyle
            />

            <div className="col-span-2">
              <Form.Item
                label="Show Salary"
                name="is_show_salary_public"
                initialValue={true}
                layout="horizontal"
                className="border-b border-t !mb-0 !py-0.5 border-b-theme-gray-700 border-t-theme-gray-700 horizontal-end"
              >
                <Switch className="custom-switch" />
              </Form.Item>
            </div>

            <Form.Item name="status" hidden initialValue="DRAFT" noStyle />

            <div className="col-span-2 mt-3">
              <div className="justify-start text-white text-xl font-bold">
                CV Processing Score Distribution :
              </div>
              <Divider className="border-slate-600 mt-3 mb-0" />
            </div>

            <Form.Item
              label="Interview Passing Rate (%)"
              name="interview_passing_rate_percentage"
              rules={[
                {
                  required: true,
                  message: "Please enter interview passing rate",
                },
                {
                  min: 1,
                  max: 100,
                  type: "number",
                  message: "Rate must be between 1 and 100",
                },
              ]}
            >
              <InputNumber
                className="transparent-input w-full"
                controls={false}
              />
            </Form.Item>

            <Form.Item
              label="CV Accepted Score (%)"
              name="cv_accepted_score_percentage"
              rules={[
                {
                  required: true,
                  message: "Please enter CV accepted score",
                },
                {
                  type: "number",
                  min: 1,
                  max: 100,
                  message: "Score must be between 1 and 100",
                },
              ]}
            >
              <InputNumber
                className="transparent-input w-full"
                controls={false}
              />
            </Form.Item>
            <Form.Item
              label="Functional Screening Keywords"
              name="functional_screening_keywords"
              rules={[
                {
                  required: true,
                  message: "Please select functional screening keywords",
                },
              ]}
            >
              <Select
                mode="tags"
                placeholder="Add keywords"
                className="tags-select"
                suffixIcon={
                  <ChevronDownIcon size={24} className="text-white" />
                }
              />
            </Form.Item>

            <Form.Item
              validateFirst
              label="Functional Keyword Weightage (%)"
              name="functional_keyword_weightage"
              dependencies={[
                "tactical_keyword_weightage",
                "prerequisite_keyword_weightage",
              ]}
              rules={[
                {
                  required: true,
                  message: "Please enter functional keyword weightage",
                },
                {
                  min: 0,
                  max: 100,
                  type: "number",
                  message: "Weightage must be between 0 and 100",
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const tactical = Number(
                      getFieldValue("tactical_keyword_weightage") || 0,
                    );
                    const prerequisite = Number(
                      getFieldValue("prerequisite_keyword_weightage") || 0,
                    );
                    const functional = Number(value || 0);
                    if (tactical + prerequisite + functional === 100) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("Total weightage must be exactly 100%"),
                    );
                  },
                }),
              ]}
            >
              <InputNumber
                controls={false}
                className="transparent-input w-full"
              />
            </Form.Item>

            <Form.Item
              label="Technical Screening Keywords"
              name="tactical_screening_keywords"
              rules={[
                {
                  required: true,
                  message: "Please select technical screening keywords",
                },
              ]}
            >
              <Select
                mode="tags"
                placeholder="Add keywords"
                className="tags-select"
                suffixIcon={
                  <ChevronDownIcon size={24} className="text-white" />
                }
              />
            </Form.Item>

            <Form.Item
              validateFirst
              label="Tactical Keyword Weightage (%)"
              name="tactical_keyword_weightage"
              dependencies={[
                "functional_keyword_weightage",
                "prerequisite_keyword_weightage",
              ]}
              rules={[
                {
                  required: true,
                  message: "Please enter tactical keyword weightage",
                },
                {
                  min: 0,
                  max: 100,
                  type: "number",
                  message: "Weightage must be between 0 and 100",
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const functional = Number(
                      getFieldValue("functional_keyword_weightage") || 0,
                    );
                    const prerequisite = Number(
                      getFieldValue("prerequisite_keyword_weightage") || 0,
                    );
                    const tactical = Number(value || 0);
                    if (functional + prerequisite + tactical === 100) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("Total weightage must be exactly 100%"),
                    );
                  },
                }),
              ]}
            >
              <InputNumber
                className="transparent-input w-full"
                controls={false}
              />
            </Form.Item>

            <Form.Item
              label="Prerequisite Screening Keywords"
              name="prerequisite_screening_keywords"
              rules={[
                {
                  required: true,
                  message: "Please select prerequisite screening keywords",
                },
              ]}
            >
              <Select
                className="tags-select"
                mode="tags"
                placeholder="Add keywords"
                suffixIcon={
                  <ChevronDownIcon size={24} className="text-white" />
                }
              />
            </Form.Item>

            <Form.Item
              validateFirst
              label="Prerequisite Keyword Weightage (%)"
              name="prerequisite_keyword_weightage"
              dependencies={[
                "functional_keyword_weightage",
                "tactical_keyword_weightage",
              ]}
              rules={[
                {
                  required: true,
                  message: "Please enter prerequisite keyword weightage",
                },
                {
                  min: 0,
                  max: 100,
                  type: "number",
                  message: "Weightage must be between 0 and 100",
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const functional = Number(
                      getFieldValue("functional_keyword_weightage") || 0,
                    );
                    const tactical = Number(
                      getFieldValue("tactical_keyword_weightage") || 0,
                    );
                    const prerequisite = Number(value || 0);
                    if (functional + tactical + prerequisite === 100) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("Total weightage must be exactly 100%"),
                    );
                  },
                }),
              ]}
            >
              <InputNumber
                className="transparent-input w-full"
                controls={false}
              />
            </Form.Item>
            <div className="col-span-2">
              <Form.Item
                label="Skills"
                name="skills"
                rules={[
                  {
                    required: true,
                    message: "Please select skills",
                  },
                ]}
              >
                <Select
                  mode="tags"
                  className="tags-select"
                  placeholder="Add skills"
                  suffixIcon={
                    <ChevronDownIcon size={24} className="text-white" />
                  }
                />
              </Form.Item>
            </div>

            <div className="col-span-2 mt-3">
              <div className="justify-start text-white text-xl font-bold">
                Other requirement :
              </div>
              <Divider className="border-slate-600 mt-3 mb-0" />
            </div>

            <Form.Item
              label="Job Context"
              name="job_context"
              rules={[
                {
                  required: true,
                  message: "Please enter job context",
                },
              ]}
              getValueFromEvent={(value: string) => value}
            >
              <ReactQuill
                theme="snow"
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Write job context here..."
              />
            </Form.Item>

            <Form.Item
              label="Job Description"
              name="job_description"
              rules={[
                {
                  required: true,
                  message: "Please enter job description",
                },
              ]}
              getValueFromEvent={(value: string) => value}
            >
              <ReactQuill
                theme="snow"
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Write job description here..."
              />
            </Form.Item>

            <Form.Item
              label="Educational Experience"
              name="educational_experience"
              rules={[
                {
                  required: true,
                  message: "Please enter educational experience",
                },
              ]}
              getValueFromEvent={(value: string) => value}
            >
              <ReactQuill
                theme="snow"
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Write educational experience here..."
              />
            </Form.Item>

            <Form.Item
              label="Experience Requirement"
              name="experience_requirement"
              rules={[
                {
                  required: true,
                  message: "Please enter experience requirement",
                },
              ]}
              getValueFromEvent={(value: string) => value}
            >
              <ReactQuill
                theme="snow"
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Write experience requirement here..."
              />
            </Form.Item>

            <Form.Item
              label="Additional Requirement"
              name="additional_requirement"
              rules={[
                {
                  required: true,
                  message: "Please enter additional requirement",
                },
              ]}
              getValueFromEvent={(value: string) => value}
            >
              <ReactQuill
                theme="snow"
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Write additional requirement here..."
              />
            </Form.Item>

            <Form.Item
              label="Compensation and Benefit"
              name="compensation_and_benefit"
              rules={[
                {
                  required: true,
                  message: "Please enter compensation and benefit",
                },
              ]}
              getValueFromEvent={(value: string) => value}
            >
              <ReactQuill
                theme="snow"
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Write compensation and benefit here..."
              />
            </Form.Item>
          </div>

          <div className="flex justify-end gap-4 mt-5">
            <Tooltip
              title={
                publishDate && !dayjs(publishDate).isAfter(dayjs(), "day")
                  ? "To save as draft, publish date must be after today."
                  : ""
              }
            >
              <Button
                size="large"
                className="btn-primary-bordered"
                onClick={() => form.setFieldValue("status", "DRAFT")}
                htmlType="submit"
                loading={isSubmitting && status === "DRAFT"}
                disabled={
                  (isSubmitting && status === "DRAFT") ||
                  (publishDate && !dayjs(publishDate).isAfter(dayjs(), "day"))
                }
              >
                Draft
              </Button>
            </Tooltip>

            <Tooltip
              title={
                publishDate && dayjs(publishDate).isAfter(dayjs(), "day")
                  ? "To publish, publish date must be today or before today."
                  : ""
              }
            >
              <Button
                size="large"
                className="btn-primary min-w-20"
                onClick={() => form.setFieldValue("status", "PUBLISHED")}
                htmlType="submit"
                loading={isSubmitting && status === "PUBLISHED"}
                disabled={
                  (isSubmitting && status === "PUBLISHED") ||
                  (publishDate && dayjs(publishDate).isAfter(dayjs(), "day"))
                }
              >
                {id && jobPostData?.data?.status !== "DRAFT"
                  ? "Update"
                  : "Publish"}
              </Button>
            </Tooltip>
          </div>
        </Form>
      </WithLoader>

      {/* Manage Department Modal */}
      {subConfig.open && subConfig.type === "MANAGE_DEPARTMENT" && (
        <ManageDepartment handleClose={handleSubClose} open={subConfig.open} />
      )}

      {/* Manage Designation Modal */}
      <ManageDesignation
        handleClose={handleSubClose}
        open={subConfig.open && subConfig.type === "MANAGE_DESIGNATION"}
      />
    </CustomDrawer>
  );
}

function CustomCheckbox({
  value,
  onChange,
  options,
  className,
}: {
  value: string | null;
  onChange: (val: string | null) => void;
  options: any;
  className?: string;
}) {
  const handleSelect = (val: string) => {
    onChange(value === val ? null : val);
  };

  return (
    <div
      className={`space-y-2 ${className || ""} max-h-[150px] overflow-y-auto`}
    >
      {options?.map((option: any) => {
        return (
          <label
            key={option?.id}
            className="flex items-center gap-2 cursor-pointer select-none text-white text-sm"
          >
            <Checkbox
              value={option?.id}
              checked={value === option?.id}
              onChange={() => handleSelect(option?.id)}
            />
            {option?.name}
          </label>
        );
      })}
    </div>
  );
}
